import { useState, useEffect, useRef, useCallback } from 'react';
import { buildDefaultSipDomain } from '@easyflow/shared';
import { 
  Terminal,
  Play,
  Pause,
  RotateCcw,
  Download,
  Search,
  Eye,
  AlertCircle,
  AlertTriangle,
  Info,
  XCircle,
  RefreshCw,
  Settings,
  Clock,
  Activity,
  Smartphone,
  Phone,
  Globe,
  Copy,
  Check,
  Shield,
  Layers,
  Trash2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { normalizeTrunkHealth } from '@easyflow/shared';
import { SipHealthBadge } from '@easyflow/ui-frontend';
import { apiClient, SIP_PUBLIC_SERVER_HOST } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import ToolMetrics from '@/components/observability/ToolMetrics';
import { IpBanCheckDialog } from '@/components/IpBanCheckDialog';
import type { SipPublicConfig } from '@easyflow/shared';

interface LogEntry {
  id: string;
  timestamp: string;
  level: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'CRITICAL';
  component: string;
  message: string;
  tenant_id?: string;
  source: 'freeswitch' | 'backend' | 'frontend' | 'system' | 'network';
}

interface LogStats {
  total: number;
  errors: number;
  warnings: number;
  last_update: string;
}

interface SipTrunkSipConfig {
  username?: string;
  server?: string;
  port?: number;
  transport?: string;
  register?: boolean;
}

// Riga trunk consumata dal tab Provisioning. `health_status` è lo stato di
// registrazione REALE derivato dal monitor SIP via ESL sul nodo FS assegnato
// al tenant (SoT `sip_trunks.health_status`), NON il flag di config.
interface SipTrunkRow {
  id: string;
  name: string;
  provider?: string;
  status?: string;
  sip_config?: SipTrunkSipConfig;
  health_status?: string | null;
  auth_mode?: 'register' | 'ip_auth' | null;
  last_successful_registration?: string | null;
}

interface ESLHealthStatus {
  state: 'connecting' | 'connected' | 'degraded' | 'disconnected';
  since: string;
  reconnectAttempts: number;
  lastEventAt: string | null;
  lastHeartbeatLatencyMs: number | null;
  uptimeSeconds: number;
  host: string;
  port: number;
  circuitBreakerOpen: boolean;
  consecutiveFailures: number;
}

interface SpamCall {
  id: string | number;
  created_at?: string;
  event_type?: string;
  ip_address?: string;
  jail?: string;
  reason?: string;
  caller_id?: string;
  target?: string;
  country?: string;
  city?: string;
  [key: string]: unknown;
}

interface ProvisioningExtension {
  id: string | number;
  extension: string;
  password: string;
  display_name: string;
  status?: string;
  [key: string]: unknown;
}

interface QueueCard {
  name: string;
  label?: string;
  hasAlerts?: boolean;
  staleCount?: number;
  isPaused?: boolean;
  counts?: { active?: number; waiting?: number; completed?: number; failed?: number; delayed?: number };
  [key: string]: unknown;
}

interface QueueStatus {
  connected?: boolean;
  queues?: QueueCard[];
  alerts?: {
    hasCritical?: boolean;
    hasWarning?: boolean;
    totalStale?: number;
    totalFailed?: number;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface QueueJob {
  id: string | number;
  name?: string;
  status?: string;
  isStale?: boolean;
  timestamp?: number | string;
  finishedOn?: number | string;
  attempts?: number;
  maxAttempts?: number;
  failedReason?: string;
  data?: {
    type?: string;
    channel?: string;
    to?: string;
    leadName?: string;
    campaignId?: string | number;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface AuditLogEntry {
  id: string | number;
  created_at?: string;
  user_id?: string;
  action?: string;
  resource_type?: string;
  resource_id?: string;
  ip_address?: string;
  status_code?: number;
  request_method?: string;
  request_path?: string;
  duration_ms?: number | null;
  user_agent?: string;
  error_message?: string;
  old_values?: Record<string, unknown> | null;
  new_values?: Record<string, unknown> | null;
  [key: string]: unknown;
}

export default function Logs() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<LogEntry[]>([]);
  const [isLive, setIsLive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLevel, setSelectedLevel] = useState<string>('all');
  const [selectedComponent, setSelectedComponent] = useState<string>('all');
  const [selectedSource, setSelectedSource] = useState<string>('all');
  const [autoScroll, setAutoScroll] = useState(true);
  const [logStats, setLogStats] = useState<LogStats>({ total: 0, errors: 0, warnings: 0, last_update: '' });
  const [activeTab, setActiveTab] = useState<'live' | 'search' | 'settings' | 'provisioning' | 'spam' | 'observability' | 'queues' | 'audit'>('live');
  
  // Spam data state
  const [spamCalls, setSpamCalls] = useState<SpamCall[]>([]);
  const [spamStats, setSpamStats] = useState<{blocked_today: number; blocked_week: number; unique_ips_today: number; by_type?: Array<{event_type: string; count: number; today: number; week: number}>} | null>(null);
  const [spamLoading, setSpamLoading] = useState(false);
  const [spamLastUpdate, setSpamLastUpdate] = useState<Date | null>(null);
  const [ipBanDialogOpen, setIpBanDialogOpen] = useState(false);
  const logsEndRef = useRef<HTMLDivElement>(null);
  
  // Provisioning state
  const [extensions, setExtensions] = useState<ProvisioningExtension[]>([]);
  const [trunks, setTrunks] = useState<SipTrunkRow[]>([]);
  const [sipPublicConfig, setSipPublicConfig] = useState<SipPublicConfig | null>(null);
  const [copiedField, setCopiedField] = useState<string>('');
  const [testingTrunk, setTestingTrunk] = useState<string | null>(null);
  
  // ESL Health state
  const [eslHealth, setEslHealth] = useState<ESLHealthStatus | null>(null);
  
  // Queue Monitor state
  const [queueStatus, setQueueStatus] = useState<QueueStatus | null>(null);
  const [queueJobs, setQueueJobs] = useState<QueueJob[]>([]);
  const [queueJobsTotal, setQueueJobsTotal] = useState(0);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueJobsLoading, setQueueJobsLoading] = useState(false);
  const [queueSelectedQueue, setQueueSelectedQueue] = useState('mass-outreach');
  const [queueSelectedStatus, setQueueSelectedStatus] = useState('failed');
  const [queueLastUpdate, setQueueLastUpdate] = useState<Date | null>(null);
  const [queueExpandedJob, setQueueExpandedJob] = useState<string | number | null>(null);
  const [queueActionLoading, setQueueActionLoading] = useState<string | null>(null);
  const [queueHistory, setQueueHistory] = useState<Record<string, Record<string, unknown>[]>>({});
  const [queueHistoryHours, setQueueHistoryHours] = useState('168');
  const [queueHistoryLoading, setQueueHistoryLoading] = useState(false);

  // Audit log state
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const auditRequestSequence = useRef(0);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotalPages, setAuditTotalPages] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditActionFilter, setAuditActionFilter] = useState('all');
  const [auditResourceFilter, setAuditResourceFilter] = useState('all');
  const [auditSearch, setAuditSearch] = useState('');
  const [auditStartDate, setAuditStartDate] = useState('');
  const [auditEndDate, setAuditEndDate] = useState('');
  const [auditResourceTypes, setAuditResourceTypes] = useState<string[]>([]);
  const [auditExpandedRow, setAuditExpandedRow] = useState<string | number | null>(null);

  // Observability metrics state
  const [socketMetrics, setSocketMetrics] = useState<{
    socketIO: { totalConnections: number; authenticatedConnections: number; roomCounts: Record<string, number> };
    esl: { state: string; reconnectAttempts: number; eventCounts: Record<string, number>; uptimeSeconds: number };
    calls: { activeCallsInMemory: number; callsByTenant: Record<string, number> };
    dlq: { pendingRetry: number; failed: number };
    uptime: { seconds: number; formatted: string };
  } | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsLastUpdate, setMetricsLastUpdate] = useState<Date | null>(null);
  
  const [settings, setSettings] = useState({
    freeswitchLevel: 'info',
    maxLogs: '1000',
    retention: '30',
    sipTracing: true,
  });
  
  const loadESLHealth = async () => {
    try {
      const response = await apiClient.get<ESLHealthStatus>('/system/esl-health');
      // Handle various response formats
      const healthData = (response?.data || response) as ESLHealthStatus | undefined;
      if (healthData && (healthData.state || healthData.host)) {
        setEslHealth(healthData as ESLHealthStatus);
      } else if (response?.success === false) {
        // API returned error - show disconnected state
        setEslHealth({
          state: 'disconnected',
          since: new Date().toISOString(),
          reconnectAttempts: 0,
          lastEventAt: null,
          lastHeartbeatLatencyMs: null,
          uptimeSeconds: 0,
          host: '127.0.0.1',
          port: 8021,
          circuitBreakerOpen: false,
          consecutiveFailures: 0
        });
      }
    } catch (error) {
      console.error('Error loading ESL health:', error);
      // Set disconnected state on error
      setEslHealth({
        state: 'disconnected',
        since: new Date().toISOString(),
        reconnectAttempts: 0,
        lastEventAt: null,
        lastHeartbeatLatencyMs: null,
        uptimeSeconds: 0,
        host: '127.0.0.1',
        port: 8021,
        circuitBreakerOpen: false,
        consecutiveFailures: 0
      });
    }
  };

  const loadSavedSettings = () => {
    try {
      const savedSettings = localStorage.getItem('logSettings');
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings) as Partial<{
          freeswitchLevel: string;
          maxLogs: string;
          retention: string;
          sipTracing: boolean;
        }>;
        // Merge con i default: ignora chiavi legacy rimosse (backendLevel/rtpCapture/verboseDebugging).
        setSettings(prev => ({
          freeswitchLevel: parsed.freeswitchLevel ?? prev.freeswitchLevel,
          maxLogs: parsed.maxLogs ?? prev.maxLogs,
          retention: parsed.retention ?? prev.retention,
          sipTracing: typeof parsed.sipTracing === 'boolean' ? parsed.sipTracing : prev.sipTracing,
        }));
      }
    } catch (error) {
      console.error('Error loading saved settings:', error);
    }
  };
  
  const loadProvisioningData = useCallback(async () => {
    try {
      const [extRes, trunkRes, sipCfgRes] = await Promise.all([
        // Il tenant arriva dalla sessione: `tenant_id` non è nel contratto query (strict → 400).
        apiClient.get<{ items?: ProvisioningExtension[] }>('/extensions?limit=1000'),
        apiClient.get<{ data?: SipTrunkRow[] } | SipTrunkRow[]>('/sip-trunks'),
        apiClient.get<SipPublicConfig>('/sip-public-config')
      ]);

      setExtensions(Array.isArray((extRes.data)?.items) ? (extRes.data)!.items! : []);
      setTrunks(Array.isArray(trunkRes.data) ? trunkRes.data : ((trunkRes.data)?.data ?? []));
      setSipPublicConfig(sipCfgRes.data ?? null);
      
      console.log('📱 Provisioning data loaded:', { 
        extensions: extRes.data, 
        trunks: trunkRes.data 
      });
    } catch (error) {
      console.error('Error loading provisioning data:', error);
    }
  }, []);
  
  const loadSpamData = async () => {
    setSpamLoading(true);
    try {
      // Use live endpoint that reads directly from fail2ban log
      const response = await apiClient.get<{ stats?: NonNullable<typeof spamStats>; calls?: SpamCall[] }>('/security/live-security-events?limit=100');
      const data = (response.data || response) as { stats?: NonNullable<typeof spamStats>; calls?: SpamCall[] } | undefined;
      
      if (data?.stats) {
        setSpamStats(data.stats);
      }
      if (data?.calls) {
        setSpamCalls(data.calls);
      }
      setSpamLastUpdate(new Date());
    } catch (error) {
      console.error('Error loading spam data:', error);
      // Fallback to old endpoints if live endpoint fails
      try {
        const [statsRes, callsRes] = await Promise.all([
          apiClient.get<{ stats?: NonNullable<typeof spamStats> }>('/security/spam-stats'),
          apiClient.get<{ calls?: SpamCall[] }>('/security/blocked-calls?limit=50')
        ]);
        setSpamStats((statsRes.data)?.stats || null);
        setSpamCalls((callsRes.data)?.calls || []);
        setSpamLastUpdate(new Date());
      } catch (e) {
        console.error('Error loading spam data fallback:', e);
      }
    } finally {
      setSpamLoading(false);
    }
  };
  
  const loadSocketMetrics = async () => {
    setMetricsLoading(true);
    try {
      const response = await apiClient.get<NonNullable<typeof socketMetrics>>('/system/socket-metrics');
      const data = (response?.data || response) as NonNullable<typeof socketMetrics> | undefined;
      if (data) {
        setSocketMetrics(data);
        setMetricsLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Error loading socket metrics:', error);
    } finally {
      setMetricsLoading(false);
    }
  };
  
  const loadQueueStatus = async () => {
    setQueueLoading(true);
    try {
      const response = await apiClient.get<{ connected?: boolean; queues?: QueueCard[]; alerts?: { hasCritical?: boolean; hasWarning?: boolean; totalStale?: number; totalFailed?: number; [key: string]: unknown }; [key: string]: unknown }>('/queue-monitor/status');
      if (response?.success || response?.data) {
        setQueueStatus((response?.data || response) as QueueStatus);
        setQueueLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Error loading queue status:', error);
    } finally {
      setQueueLoading(false);
    }
  };

  const loadQueueHistory = useCallback(async (hours?: string) => {
    setQueueHistoryLoading(true);
    try {
      const h = hours || queueHistoryHours;
      const queueNames = ['workflows', 'mass-outreach', 'mass-email', 'messaging'];
      const results: Record<string, Record<string, unknown>[]> = {};
      await Promise.all(queueNames.map(async (qName) => {
        try {
          const response = await apiClient.get<Record<string, unknown>[]>(`/queue-monitor/history/${qName}?hours=${h}`);
          if (response?.success || response?.data) {
            results[qName] = (response?.data || []);
          }
        } catch {
          results[qName] = [];
        }
      }));
      setQueueHistory(results);
    } catch (error) {
      console.error('Error loading queue history:', error);
    } finally {
      setQueueHistoryLoading(false);
    }
  }, [queueHistoryHours]);

  const updateStats = (logData: LogEntry[]) => {
    const stats: LogStats = {
      total: logData.length,
      errors: logData.filter(log => log.level === 'ERROR' || log.level === 'CRITICAL').length,
      warnings: logData.filter(log => log.level === 'WARN').length,
      last_update: new Date().toLocaleString()
    };
    setLogStats(stats);
  };

  // Mappa la sorgente selezionata all'endpoint reale corretto: FreeSWITCH via
  // ESL (FS-1/FS-2), nginx da FE-1, journald/PM2 del nodo API. 'all' aggrega
  // system+backend+nginx (NON FreeSWITCH: finestra ESL lenta, on-demand).
  const sourceToLogsEndpoint = useCallback((source: string, lines: number): string => {
    switch (source) {
      case 'freeswitch': return `/system-logs/sip?lines=${lines}`;
      case 'network': return `/system-logs/network?lines=${lines}`;
      case 'system': return `/system-logs/system?lines=${lines}`;
      case 'backend': return `/system-logs/backend?lines=${lines}`;
      default: return `/system-logs/all?lines=${lines}`;
    }
  }, []);

  const loadInitialLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get<{ logs?: LogEntry[] }>(sourceToLogsEndpoint(selectedSource, 100));

      // Handle response - data contains { logs, count }
      const logsData = (response?.data)?.logs || (response as { logs?: LogEntry[] })?.logs || [];
      if (Array.isArray(logsData)) {
        setLogs(logsData);
        updateStats(logsData);
      } else {
        setLogs([]);
        updateStats([]);
      }
    } catch (error) {
      console.error('Error loading logs:', error);
      setLogs([]);
      updateStats([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSource, sourceToLogsEndpoint]);


  // Ricarica i log quando cambia la sorgente selezionata (endpoint reale diverso).
  useEffect(() => {
    loadInitialLogs();
  }, [loadInitialLogs]);

  useEffect(() => {
    loadSavedSettings();
    loadProvisioningData();
    loadESLHealth();
    loadSpamData();
    loadSocketMetrics();
    loadQueueStatus();
    loadQueueHistory();
    
    // Poll ESL health every 10 seconds
    const eslInterval = setInterval(loadESLHealth, 10000);
    
    // Poll spam data every 30 seconds
    const spamInterval = setInterval(loadSpamData, 30000);
    
    // Poll socket metrics every 30 seconds
    const metricsInterval = setInterval(loadSocketMetrics, 30000);

    // Poll queue status every 15 seconds
    const queueInterval = setInterval(loadQueueStatus, 15000);
    
    return () => {
      clearInterval(eslInterval);
      clearInterval(spamInterval);
      clearInterval(metricsInterval);
      clearInterval(queueInterval);
    };
  }, [loadProvisioningData, loadQueueHistory]);

  const loadQueueJobs = async (queue?: string, status?: string) => {
    setQueueJobsLoading(true);
    try {
      const q = queue || queueSelectedQueue;
      const s = status || queueSelectedStatus;
      const response = await apiClient.get<{ jobs?: QueueJob[]; total?: number }>(`/queue-monitor/jobs/${q}?status=${s}&limit=100`);
      if (response?.success || response?.data) {
        const data = (response?.data || response) as { jobs?: QueueJob[]; total?: number } | undefined;
        setQueueJobs(data?.jobs || []);
        setQueueJobsTotal(data?.total || 0);
      }
    } catch (error) {
      console.error('Error loading queue jobs:', error);
      setQueueJobs([]);
    } finally {
      setQueueJobsLoading(false);
    }
  };

  const handleQueueAction = async (queueName: string, action: string, jobId?: string | number) => {
    const actionKey = jobId ? `${action}-${jobId}` : `${action}-${queueName}`;
    setQueueActionLoading(actionKey);
    try {
      const response = await apiClient.post<{ message?: string }>(`/queue-monitor/action/${queueName}`, { action, jobId });
      if (response?.success) {
        toast({ title: 'Operazione completata', description: response?.message || (response?.data as { message?: string } | undefined)?.message || 'Azione eseguita' });
        await loadQueueStatus();
        await loadQueueJobs();
      } else {
        toast({ title: 'Errore', description: response?.error || 'Operazione fallita', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Errore', description: (error as Error)?.message || 'Operazione fallita', variant: 'destructive' });
    } finally {
      setQueueActionLoading(null);
    }
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(''), 2000);
    toast({ title: "Copiato!", description: `${field} copiato negli appunti` });
  };

  const generateSIPConfig = (ext: { extension: string; password: string; display_name: string; [key: string]: unknown }) => {
    // Dominio/realm per-tenant (corretto): derivato dallo slug. Server e porte
    // vengono dal registrar SBC pubblico (/sip-public-config), MAI dall'apex
    // `easyflowai.it` (Cloudflare-proxied, non raggiunge il SBC).
    const sipDomain = user?.tenantSlug ? buildDefaultSipDomain(user.tenantSlug) : '';
    const sipServer = sipPublicConfig?.sipServer ?? SIP_PUBLIC_SERVER_HOST;
    const sipPort = sipPublicConfig?.sipPortUdp ?? 5060;
    const outboundProxy = sipPublicConfig?.outboundProxy ?? '';

    return {
      username: ext.extension,
      password: ext.password,
      domain: sipDomain,
      server: sipServer,
      port: sipPort,
      outboundProxy,
      transport: 'UDP',
      codec: 'PCMU,PCMA,G729',
      displayName: ext.display_name
    };
  };

  const downloadConfig = (ext: { extension: string; password: string; display_name: string; [key: string]: unknown }, format: 'xml' | 'json') => {
    const config = generateSIPConfig(ext);
    const content = format === 'xml' 
      ? `<?xml version="1.0" encoding="UTF-8"?>
<account>
  <displayName>${config.displayName}</displayName>
  <username>${config.username}</username>
  <password>${config.password}</password>
  <domain>${config.domain}</domain>
  <server>${config.server}</server>
  <port>${config.port}</port>
  <outboundProxy>${config.outboundProxy}</outboundProxy>
  <transport>${config.transport}</transport>
  <codec>${config.codec}</codec>
</account>`
      : JSON.stringify(config, null, 2);
    
    const blob = new Blob([content], { type: format === 'xml' ? 'text/xml' : 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `extension-${ext.extension}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Download", description: `Configurazione scaricata` });
  };

  const testTrunkConnection = async (trunkId: string) => {
    setTestingTrunk(trunkId);
    try {
      const response = await apiClient.post(`/sip-trunks/${trunkId}/test-connection`);
      
      if (response.success) {
        toast({ 
          title: "✅ Trunk OK", 
          description: "Connessione trunk verificata" 
        });
      } else {
        toast({ 
          title: "⚠️ Trunk Error", 
          description: response.message || "Trunk non raggiungibile", 
          variant: "destructive" 
        });
      }
    } catch (error) {
      toast({ 
        title: "Errore Test", 
        description: (error as Error).message, 
        variant: "destructive" 
      });
    } finally {
      setTestingTrunk(null);
    }
  };

  const filterLogs = useCallback(() => {
    let filtered = logs;

    if (searchTerm) {
      filtered = filtered.filter(log => 
        log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.component.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedLevel !== 'all') {
      filtered = filtered.filter(log => log.level === selectedLevel);
    }

    if (selectedComponent !== 'all') {
      filtered = filtered.filter(log => log.component === selectedComponent);
    }

    if (selectedSource !== 'all') {
      filtered = filtered.filter(log => log.source === selectedSource);
    }

    setFilteredLogs(filtered);
  }, [logs, searchTerm, selectedComponent, selectedLevel, selectedSource]);

  useEffect(() => {
    filterLogs();
  }, [logs, searchTerm, selectedLevel, selectedComponent, selectedSource, filterLogs]);

  const scrollToBottom = () => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (autoScroll) {
      scrollToBottom();
    }
  }, [filteredLogs, autoScroll]);

  const addNewLog = async () => {
    try {
      // Fetch new logs from real API, per la sorgente selezionata.
      const response = await apiClient.get<{ logs?: LogEntry[] }>(sourceToLogsEndpoint(selectedSource, 20));
      const logsData = (response?.data)?.logs || (response as { logs?: LogEntry[] })?.logs || [];
      
      if (Array.isArray(logsData) && logsData.length > 0) {
        setLogs(prev => {
          // Filter out duplicates using stable IDs
          const existingIds = new Set(prev.map(l => l.id));
          const newLogs = logsData.filter((log: LogEntry) => !existingIds.has(log.id));
          if (newLogs.length > 0) {
            const updated = [...newLogs, ...prev].slice(0, 1000);
            // Compute stats synchronously with the new array
            const stats: LogStats = {
              total: updated.length,
              errors: updated.filter(l => l.level === 'ERROR' || l.level === 'CRITICAL').length,
              warnings: updated.filter(l => l.level === 'WARN').length,
              last_update: new Date().toLocaleString()
            };
            setLogStats(stats);
            return updated;
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('Error fetching new logs:', error);
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isLive) {
      interval = setInterval(() => {
        addNewLog();
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [isLive]);

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'ERROR':
      case 'CRITICAL':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'WARN':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'INFO':
        return <Info className="h-4 w-4 text-blue-500" />;
      case 'DEBUG':
        return <Eye className="h-4 w-4 text-muted-foreground" />;
      default:
        return <Info className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getLevelBadge = (level: string) => {
    const colors = {
      'CRITICAL': 'bg-red-100 text-red-800',
      'ERROR': 'bg-red-100 text-red-800',
      'WARN': 'bg-yellow-100 text-yellow-800',
      'INFO': 'bg-blue-100 text-blue-800',
      'DEBUG': 'bg-muted text-foreground'
    };
    
    return (
      <Badge className={`${colors[level as keyof typeof colors] || colors.DEBUG} text-xs`}>
        {level}
      </Badge>
    );
  };

  const getSourceBadge = (source: string) => {
    const colors = {
      'freeswitch': 'bg-purple-100 text-purple-800',
      'backend': 'bg-green-100 text-green-800',
      'frontend': 'bg-blue-100 text-blue-800',
      'system': 'bg-slate-100 text-slate-800',
      'network': 'bg-amber-100 text-amber-800'
    };
    
    return (
      <Badge className={`${colors[source as keyof typeof colors] || colors.frontend} text-xs`}>
        {source.toUpperCase()}
      </Badge>
    );
  };

  const handleRefresh = () => {
    loadInitialLogs();
  };

  const handleDownload = () => {
    const logText = filteredLogs.map(log => 
      `[${log.timestamp}] ${log.level} [${log.component}] ${log.message}`
    ).join('\n');
    
    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `voip-logs-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleClearLogs = () => {
    if (confirm('Are you sure you want to clear all logs?')) {
      setLogs([]);
      setFilteredLogs([]);
      setLogStats({ total: 0, errors: 0, warnings: 0, last_update: new Date().toLocaleString() });
    }
  };

  const handleSetLogLevel = async (level: string) => {
    try {
      const response = await apiClient.post<{ message?: string }>('/logs/freeswitch/level', { level });
      if (response?.success) {
        toast({
          title: 'Log level FreeSWITCH aggiornato',
          description: response?.message || (response?.data as { message?: string } | undefined)?.message || `Impostato a "${level}" sui nodi FS`,
        });
      } else {
        toast({
          title: 'Errore',
          description: response?.error || 'Impossibile impostare il log level sui nodi FreeSWITCH',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: (error as Error)?.message || 'Impossibile impostare il log level',
        variant: 'destructive',
      });
    }
  };

  const handleSetSipTracing = async (enabled: boolean) => {
    try {
      const response = await apiClient.post<{ message?: string }>('/logs/freeswitch/tracing', { enabled });
      if (response?.success) {
        toast({
          title: `SIP tracing ${enabled ? 'abilitato' : 'disabilitato'}`,
          description: response?.message || (response?.data as { message?: string } | undefined)?.message || 'Applicato sui nodi FreeSWITCH',
        });
      } else {
        toast({
          title: 'Errore',
          description: response?.error || 'Impossibile aggiornare il SIP tracing sui nodi FreeSWITCH',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: (error as Error)?.message || 'Impossibile aggiornare il SIP tracing',
        variant: 'destructive',
      });
    }
  };

  const loadAuditLogs = useCallback(async (page = 1) => {
    const sequence = ++auditRequestSequence.current;
    setAuditLoading(true);
    try {
      const params: Record<string, string | number> = { page, limit: 50 };
      if (auditActionFilter !== 'all') params.action = auditActionFilter;
      if (auditResourceFilter !== 'all') params.resourceType = auditResourceFilter;
      if (auditSearch) params.search = auditSearch;
      if (auditStartDate) params.startDate = auditStartDate;
      if (auditEndDate) params.endDate = auditEndDate;
      const qs = new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString();
      const response = await apiClient.get<AuditLogEntry[]>(`/audit-log?${qs}`);
      if (sequence !== auditRequestSequence.current) return;
      setAuditLogs(response.data ?? []);
      if (response.pagination) {
        setAuditTotalPages(response.pagination.totalPages || 1);
        setAuditTotal(response.pagination.total);
        setAuditPage(response.pagination.page);
      }
    } catch (error) {
      if (sequence !== auditRequestSequence.current) return;
      console.error('Error loading audit logs:', error);
      setAuditLogs([]);
    } finally {
      if (sequence === auditRequestSequence.current) setAuditLoading(false);
    }
  }, [auditActionFilter, auditEndDate, auditResourceFilter, auditSearch, auditStartDate, user?.tenantSlug]);

  const loadAuditResourceTypes = useCallback(async () => {
    try {
      const response = await apiClient.get<{ data?: string[] } | string[]>(`/audit-log/resource-types`);
      const data = (response?.data || response) as { data?: string[] } | string[] | undefined;
      if (data && !Array.isArray(data) && Array.isArray(data.data)) {
        setAuditResourceTypes(data.data);
      } else if (Array.isArray(data)) {
        setAuditResourceTypes(data);
      }
    } catch (error) {
      console.error('Error loading audit resource types:', error);
    }
  }, [user?.tenantSlug]);

  const handleAuditExport = async () => {
    try {
      const params: Record<string, string> = {};
      if (auditActionFilter !== 'all') params.action = auditActionFilter;
      if (auditResourceFilter !== 'all') params.resourceType = auditResourceFilter;
      if (auditSearch) params.search = auditSearch;
      if (auditStartDate) params.startDate = auditStartDate;
      if (auditEndDate) params.endDate = auditEndDate;
      const queryStr = new URLSearchParams(params).toString();
      const { blob } = await apiClient.download(`/audit-log/export${queryStr ? '?' + queryStr : ''}`);
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      try {
        link.href = blobUrl;
        link.download = `audit-log-${Date.now()}.csv`;
        document.body.appendChild(link);
        link.click();
      } finally {
        link.remove();
        URL.revokeObjectURL(blobUrl);
      }
      toast({ title: 'Export', description: 'Download CSV avviato' });
    } catch (error) {
      toast({ title: 'Errore', description: 'Errore durante l\'export', variant: 'destructive' });
    }
  };

  const getStatusBadge = (statusCode: number | null | undefined) => {
    if (!statusCode) return <Badge className="bg-muted text-foreground text-xs">N/A</Badge>;
    if (statusCode >= 200 && statusCode < 300) return <Badge className="bg-green-100 text-green-800 text-xs">{statusCode}</Badge>;
    if (statusCode >= 300 && statusCode < 500) return <Badge className="bg-yellow-100 text-yellow-800 text-xs">{statusCode}</Badge>;
    return <Badge className="bg-red-100 text-red-800 text-xs">{statusCode}</Badge>;
  };

  const getActionLabel = (action: string | undefined) => {
    const labels: Record<string, string> = {
      create: 'Creazione',
      update: 'Modifica',
      delete: 'Eliminazione',
      user_login: 'Login',
      user_logout: 'Logout',
      config_change: 'Config'
    };
    return (action ? labels[action] : undefined) || action;
  };

  useEffect(() => {
    if (activeTab === 'audit') {
      loadAuditLogs(1);
      loadAuditResourceTypes();
    }
    return () => { auditRequestSequence.current += 1; };
  }, [activeTab, loadAuditLogs, loadAuditResourceTypes]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Logs & Verbose</h1>
          <p className="text-muted-foreground">Monitor system logs and debugging information</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" onClick={handleDownload}>
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
          <Button variant="outline" onClick={handleClearLogs} className="text-red-600 hover:text-red-700">
            <RotateCcw className="h-4 w-4 mr-2" />
            Clear
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Logs</p>
                <p className="text-2xl font-bold text-foreground">{logStats.total}</p>
              </div>
              <Activity className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Errors</p>
                <p className="text-2xl font-bold text-red-600">{logStats.errors}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Warnings</p>
                <p className="text-2xl font-bold text-yellow-600">{logStats.warnings}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Last Update</p>
                <p className="text-sm font-bold text-foreground">{logStats.last_update}</p>
              </div>
              <Clock className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ESL Connection Status */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${
                  eslHealth?.state === 'connected' ? 'bg-green-500' :
                  eslHealth?.state === 'degraded' ? 'bg-yellow-500 animate-pulse' :
                  eslHealth?.state === 'connecting' ? 'bg-blue-500 animate-pulse' :
                  'bg-red-500'
                }`} />
                <span className="text-sm font-medium text-muted-foreground">ESL Connection</span>
                <Badge className={`text-xs ${
                  eslHealth?.state === 'connected' ? 'bg-green-100 text-green-800' :
                  eslHealth?.state === 'degraded' ? 'bg-yellow-100 text-yellow-800' :
                  eslHealth?.state === 'connecting' ? 'bg-blue-100 text-blue-800' :
                  eslHealth?.state === 'disconnected' ? 'bg-red-100 text-red-800' :
                  'bg-muted text-muted-foreground'
                }`}>
                  {eslHealth?.state?.toUpperCase() || (isLoading ? 'LOADING...' : 'DISCONNECTED')}
                </Badge>
              </div>
              {eslHealth && (
                <>
                  <div className="text-xs text-muted-foreground">
                    <span className="font-medium">Host:</span> {eslHealth.host}:{eslHealth.port}
                  </div>
                  {eslHealth.uptimeSeconds > 0 && (
                    <div className="text-xs text-muted-foreground">
                      <span className="font-medium">Uptime:</span> {Math.floor(eslHealth.uptimeSeconds / 60)}m {eslHealth.uptimeSeconds % 60}s
                    </div>
                  )}
                  {eslHealth.lastHeartbeatLatencyMs !== null && (
                    <div className={`text-xs ${eslHealth.lastHeartbeatLatencyMs > 1000 ? 'text-yellow-600' : 'text-muted-foreground'}`}>
                      <span className="font-medium">Latency:</span> {eslHealth.lastHeartbeatLatencyMs}ms
                    </div>
                  )}
                  {eslHealth.reconnectAttempts > 0 && (
                    <div className="text-xs text-orange-600">
                      <span className="font-medium">Reconnects:</span> {eslHealth.reconnectAttempts}
                    </div>
                  )}
                  {eslHealth.circuitBreakerOpen && (
                    <Badge className="bg-red-100 text-red-800 text-xs">
                      <Shield className="h-3 w-3 mr-1" />
                      Circuit Breaker Open
                    </Badge>
                  )}
                </>
              )}
            </div>
            <Activity className={`h-6 w-6 ${
              eslHealth?.state === 'connected' ? 'text-green-500' :
              eslHealth?.state === 'degraded' ? 'text-yellow-500' :
              'text-muted-foreground'
            }`} />
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Terminal className="h-5 w-5 mr-2" />
            System Logs
          </CardTitle>
          <CardDescription>
            Monitoraggio infrastruttura cluster: log FreeSWITCH (FS-1/FS-2), backend (PM2) e nginx (FE-1).
            Dati a livello di sistema/nodo, non filtrati per singolo tenant.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as typeof activeTab)}>
            <TabsList className="grid w-full grid-cols-8">
              <TabsTrigger value="live">Live Monitoring</TabsTrigger>
              <TabsTrigger value="search">Search & Filter</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
              <TabsTrigger value="provisioning">Provisioning</TabsTrigger>
              <TabsTrigger value="spam">Spam</TabsTrigger>
              <TabsTrigger value="observability">Osservabilità</TabsTrigger>
              <TabsTrigger value="queues" className="relative">
                Gestione Code
                {queueStatus?.alerts?.hasCritical && (
                  <span className="absolute -top-1 -right-1 h-2.5 w-2.5 bg-red-500 rounded-full animate-pulse" />
                )}
                {!queueStatus?.alerts?.hasCritical && queueStatus?.alerts?.hasWarning && (
                  <span className="absolute -top-1 -right-1 h-2.5 w-2.5 bg-yellow-500 rounded-full animate-pulse" />
                )}
              </TabsTrigger>
              <TabsTrigger value="audit">
                <Shield className="h-4 w-4 mr-1" />
                Audit Log
              </TabsTrigger>
            </TabsList>

            <TabsContent value="live" className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={isLive}
                      onCheckedChange={setIsLive}
                    />
                    <span className="text-sm font-medium">
                      {isLive ? 'Live Mode ON' : 'Live Mode OFF'}
                    </span>
                    {isLive ? (
                      <Play className="h-4 w-4 text-green-500" />
                    ) : (
                      <Pause className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={autoScroll}
                      onCheckedChange={setAutoScroll}
                    />
                    <span className="text-sm font-medium">Auto Scroll</span>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">
                  Showing {filteredLogs.length} of {logs.length} logs
                </div>
              </div>

              {/* Live Logs Display */}
              <div className="bg-black text-green-400 font-mono text-sm rounded-lg p-4 h-96 overflow-y-auto">
                {filteredLogs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500">
                    <Terminal className="h-10 w-10 mb-3 opacity-40" />
                    <p className="text-sm mb-1">Nessun log disponibile</p>
                    <p className="text-xs text-gray-600">Attiva Live Mode per il monitoraggio in tempo reale</p>
                    {!isLive && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3 text-green-400 border-green-600 hover:bg-green-900/20"
                        onClick={() => setIsLive(true)}
                      >
                        <Play className="h-3 w-3 mr-1" />
                        Attiva Live
                      </Button>
                    )}
                  </div>
                ) : (
                  filteredLogs.map((log) => (
                    <div key={log.id} className="flex items-start space-x-2 py-1 border-b border-gray-800">
                      <span className="text-gray-500 text-xs whitespace-nowrap">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </span>
                      <div className="flex items-center space-x-1">
                        {getLevelIcon(log.level)}
                        {getLevelBadge(log.level)}
                        {getSourceBadge(log.source)}
                      </div>
                      <span className="text-blue-400 font-semibold">[{log.component}]</span>
                      <span className="text-white flex-1">{log.message}</span>
                    </div>
                  ))
                )}
                <div ref={logsEndRef} />
              </div>
            </TabsContent>

            <TabsContent value="search" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search logs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium">Level</label>
                  <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Levels</SelectItem>
                      <SelectItem value="DEBUG">Debug</SelectItem>
                      <SelectItem value="INFO">Info</SelectItem>
                      <SelectItem value="WARN">Warning</SelectItem>
                      <SelectItem value="ERROR">Error</SelectItem>
                      <SelectItem value="CRITICAL">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium">Component</label>
                  <Select value={selectedComponent} onValueChange={setSelectedComponent}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Components</SelectItem>
                      <SelectItem value="sofia">Sofia</SelectItem>
                      <SelectItem value="mod_dptools">Mod Dptools</SelectItem>
                      <SelectItem value="api">API</SelectItem>
                      <SelectItem value="auth">Authentication</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium">Source</label>
                  <Select value={selectedSource} onValueChange={setSelectedSource}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sources</SelectItem>
                      <SelectItem value="freeswitch">FreeSWITCH (FS-1/FS-2)</SelectItem>
                      <SelectItem value="system">System (journald nodo API)</SelectItem>
                      <SelectItem value="backend">Backend (PM2)</SelectItem>
                      <SelectItem value="network">Network (nginx FE-1)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Search Results */}
              <div className="space-y-2">
                {filteredLogs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <Terminal className="h-12 w-12 mb-4 opacity-50" />
                    <p className="text-lg font-medium">Nessun log disponibile</p>
                    <p className="text-sm">I log verranno visualizzati quando disponibili dal sistema.</p>
                    <p className="text-xs mt-2 text-muted-foreground">Attiva la modalità Live per il monitoraggio in tempo reale.</p>
                  </div>
                ) : (
                  filteredLogs.map((log) => (
                    <Card key={log.id} className="p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            {getLevelIcon(log.level)}
                            {getLevelBadge(log.level)}
                            {getSourceBadge(log.source)}
                            <span className="text-sm text-muted-foreground">
                              {new Date(log.timestamp).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-sm font-semibold text-blue-600">[{log.component}]</span>
                          </div>
                          <p className="text-sm text-muted-foreground">{log.message}</p>
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>

            <TabsContent value="settings" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Log Settings</CardTitle>
                  <CardDescription>
                    I controlli FreeSWITCH (log level e SIP tracing) agiscono in tempo reale via ESL su tutti i nodi FS attivi (FS-1/FS-2).
                    Max entries e retention sono preferenze locali della dashboard.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">FreeSWITCH Log Level (FS-1/FS-2)</label>
                      <Select 
                        value={settings.freeswitchLevel} 
                        onValueChange={(value) => setSettings(prev => ({ ...prev, freeswitchLevel: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="debug">Debug</SelectItem>
                          <SelectItem value="info">Info</SelectItem>
                          <SelectItem value="notice">Notice</SelectItem>
                          <SelectItem value="warning">Warning</SelectItem>
                          <SelectItem value="error">Error</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium">Max Log Entries (dashboard)</label>
                      <Input 
                        type="number" 
                        value={settings.maxLogs}
                        onChange={(e) => setSettings(prev => ({ ...prev, maxLogs: e.target.value }))}
                        placeholder="1000" 
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium">Log Retention (giorni, dashboard)</label>
                      <Input 
                        type="number" 
                        value={settings.retention}
                        onChange={(e) => setSettings(prev => ({ ...prev, retention: e.target.value }))}
                        placeholder="30" 
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Switch 
                        checked={settings.sipTracing}
                        onCheckedChange={(checked) => {
                          setSettings(prev => ({ ...prev, sipTracing: checked }));
                          handleSetSipTracing(checked);
                        }}
                      />
                      <span className="text-sm font-medium">Abilita SIP Tracing (FS-1/FS-2)</span>
                    </div>
                  </div>

                  <div className="pt-4">
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => {
                        // Applica realmente log level + SIP tracing sui nodi FS (toast dai relativi handler).
                        handleSetLogLevel(settings.freeswitchLevel);
                        handleSetSipTracing(settings.sipTracing);
                        // Persiste le preferenze locali della dashboard.
                        localStorage.setItem('logSettings', JSON.stringify(settings));
                        toast({ title: 'Preferenze salvate', description: 'Impostazioni dashboard salvate in locale.' });
                      }}
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Applica impostazioni
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* PROVISIONING TAB */}
            <TabsContent value="provisioning" className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900">Provisioning SIP</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Configurazioni rapide per estensioni e trunk SIP. Server, porte e outbound proxy puntano al SBC pubblico
                      (Kamailio KAM-1), non ai singoli nodi FreeSWITCH. Usa questi dati per configurare softphone e dispositivi VoIP.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* EXTENSIONS SECTION */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Smartphone className="h-5 w-5" />
                    Extensions Provisioning
                  </CardTitle>
                  <CardDescription>
                    Configurazioni SIP per estensioni telefoniche
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {extensions.map((ext) => {
                    const config = generateSIPConfig(ext);
                    
                    return (
                      <Card key={ext.id} className="border-l-4 border-l-blue-500">
                        <CardContent className="pt-4">
                          <div className="flex items-center gap-3 mb-3">
                            <Phone className="h-5 w-5 text-blue-600" />
                            <h3 className="text-lg font-semibold">{ext.display_name}</h3>
                            <Badge variant={ext.status === 'active' ? 'default' : 'secondary'}>
                              {ext.status}
                            </Badge>
                          </div>
                          
                          <div className="grid md:grid-cols-2 gap-3 bg-muted p-3 rounded-lg text-sm">
                            <div>
                              <span className="text-muted-foreground">Username:</span>
                              <div className="flex items-center gap-2 mt-1">
                                <code className="bg-card px-2 py-1 rounded border flex-1 text-xs">
                                  {config.username}
                                </code>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleCopy(config.username, 'Username')}
                                >
                                  {copiedField === 'Username' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                </Button>
                              </div>
                            </div>
                            
                            <div>
                              <span className="text-muted-foreground">Password:</span>
                              <div className="flex items-center gap-2 mt-1">
                                <code className="bg-card px-2 py-1 rounded border flex-1 text-xs">
                                  {config.password}
                                </code>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleCopy(config.password, 'Password')}
                                >
                                  {copiedField === 'Password' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                </Button>
                              </div>
                            </div>
                            
                            <div>
                              <span className="text-muted-foreground">Server:</span>
                              <div className="flex items-center gap-2 mt-1">
                                <code className="bg-card px-2 py-1 rounded border flex-1 text-xs">
                                  {config.server}:{config.port}
                                </code>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleCopy(`${config.server}:${config.port}`, 'Server')}
                                >
                                  {copiedField === 'Server' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                </Button>
                              </div>
                            </div>
                            
                            <div>
                              <span className="text-muted-foreground">Domain:</span>
                              <div className="flex items-center gap-2 mt-1">
                                <code className="bg-card px-2 py-1 rounded border flex-1 text-xs">
                                  {config.domain}
                                </code>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleCopy(config.domain, 'Domain')}
                                >
                                  {copiedField === 'Domain' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                </Button>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex gap-2 mt-3">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => downloadConfig(ext, 'xml')}
                            >
                              <Download className="h-3 w-3 mr-2" />
                              XML
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => downloadConfig(ext, 'json')}
                            >
                              <Download className="h-3 w-3 mr-2" />
                              JSON
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                  
                  {extensions.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Smartphone className="h-10 w-10 mx-auto mb-3 opacity-50" />
                      <p>Nessuna estensione trovata</p>
                      <p className="text-xs mt-1">Le estensioni potrebbero essere disabilitate</p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* TRUNKS SECTION */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    SIP Trunks Provisioning
                  </CardTitle>
                  <CardDescription>
                    Configurazione e test trunk SIP per chiamate esterne
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {trunks.map((trunk) => {
                    const sipConfig: SipTrunkSipConfig = trunk.sip_config || {};
                    
                    return (
                      <Card key={trunk.id} className="border-l-4 border-l-green-500">
                        <CardContent className="pt-4">
                          <div className="flex items-center gap-3 mb-3">
                            <Globe className="h-5 w-5 text-green-600" />
                            <h3 className="text-lg font-semibold">{trunk.name}</h3>
                            <Badge variant={trunk.status === 'active' ? 'default' : 'secondary'}>
                              {trunk.status}
                            </Badge>
                            <Badge variant="outline">{trunk.provider}</Badge>
                          </div>
                          
                          <div className="grid md:grid-cols-2 gap-3 bg-muted p-3 rounded-lg text-sm">
                            <div>
                              <span className="text-muted-foreground">Username:</span>
                              <code className="block bg-card px-2 py-1 rounded border mt-1 text-xs">
                                {sipConfig.username || 'N/A'}
                              </code>
                            </div>
                            
                            <div>
                              <span className="text-muted-foreground">Server:</span>
                              <code className="block bg-card px-2 py-1 rounded border mt-1 text-xs">
                                {sipConfig.server || 'N/A'}:{sipConfig.port || 5060}
                              </code>
                            </div>
                            
                            <div>
                              <span className="text-muted-foreground">Transport:</span>
                              <Badge variant="outline" className="mt-1">{sipConfig.transport || 'UDP'}</Badge>
                            </div>
                            
                            <div>
                              <span className="text-muted-foreground">Registrazione (nodo FS assegnato):</span>
                              <div className="mt-1">
                                <SipHealthBadge
                                  status={normalizeTrunkHealth(trunk.health_status)}
                                  authMode={trunk.auth_mode ?? null}
                                  registrationState={trunk.last_successful_registration ? 'registered' : null}
                                />
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex gap-2 mt-3">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => testTrunkConnection(trunk.id)}
                              disabled={testingTrunk === trunk.id}
                            >
                              {testingTrunk === trunk.id ? (
                                <>Testing...</>
                              ) : (
                                <>
                                  <Settings className="h-3 w-3 mr-2" />
                                  Test Connection
                                </>
                              )}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                  
                  {trunks.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Globe className="h-10 w-10 mx-auto mb-3 opacity-50" />
                      <p>Nessun trunk configurato</p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* SECURITY WARNING */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div className="flex-1 text-sm">
                    <h4 className="font-semibold text-yellow-900">Nota Sicurezza</h4>
                    <p className="text-yellow-700 mt-1">
                      Le connessioni sono accettate solo da IP autorizzati in whitelist. 
                      Vai su Security Dashboard per gestire gli IP autorizzati.
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="spam" className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Shield className="h-5 w-5 text-red-500" />
                  Eventi di Sicurezza
                  <span className="flex items-center gap-1.5 text-xs font-normal text-muted-foreground ml-2">
                    <span className="h-2 w-2 bg-green-500 rounded-full animate-pulse" />
                    Auto-refresh 30s
                    {spamLastUpdate && (
                      <span className="text-muted-foreground">
                        | Ultimo: {spamLastUpdate.toLocaleTimeString()}
                      </span>
                    )}
                  </span>
                </h3>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setIpBanDialogOpen(true)}>
                    <Shield className="h-4 w-4 mr-2" />
                    Verifica IP
                  </Button>
                  <Button variant="outline" size="sm" onClick={loadSpamData} disabled={spamLoading}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${spamLoading ? 'animate-spin' : ''}`} />
                    Aggiorna
                  </Button>
                </div>
              </div>

              {spamStats && (
                <div className="grid grid-cols-3 gap-4">
                  <Card className="border-l-4 border-l-red-500">
                    <CardContent className="pt-4">
                      <div className="text-2xl font-bold text-red-600">{spamStats.blocked_today}</div>
                      <div className="text-sm text-muted-foreground">Eventi Oggi</div>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-orange-500">
                    <CardContent className="pt-4">
                      <div className="text-2xl font-bold text-orange-600">{spamStats.blocked_week}</div>
                      <div className="text-sm text-muted-foreground">Questa Settimana</div>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-yellow-500">
                    <CardContent className="pt-4">
                      <div className="text-2xl font-bold text-yellow-600">{spamStats.unique_ips_today}</div>
                      <div className="text-sm text-muted-foreground">IP Unici Oggi</div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {spamStats?.by_type && spamStats.by_type.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {spamStats.by_type.map((t) => (
                    <div key={t.event_type} className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2">
                      <span className={`inline-block w-3 h-3 rounded-full ${
                        t.event_type === 'fail2ban_ban' ? 'bg-red-500' :
                        t.event_type === 'spoofed_call_blocked' ? 'bg-orange-500' :
                        t.event_type === 'rate_limit_exceeded' ? 'bg-yellow-500' :
                        'bg-muted-foreground'
                      }`} />
                      <span className="text-sm font-medium">{t.event_type.replace(/_/g, ' ')}</span>
                      <span className="text-xs text-muted-foreground">({t.count} totali, {t.today} oggi)</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="bg-black text-green-400 font-mono text-sm rounded-lg p-4 max-h-96 overflow-auto">
                {spamLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin" />
                  </div>
                ) : spamCalls.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <Shield className="h-10 w-10 mb-3 opacity-40" />
                    <p className="text-sm">Nessun evento di sicurezza registrato</p>
                    <p className="text-xs text-muted-foreground mt-1">Gli eventi di sicurezza (ban, blocchi, rate limit) appariranno qui</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700 text-left text-xs text-gray-500 uppercase">
                        <th className="pb-2">Timestamp</th>
                        <th className="pb-2">Tipo</th>
                        <th className="pb-2">IP Sorgente</th>
                        <th className="pb-2">Dettagli</th>
                        <th className="pb-2">Posizione</th>
                      </tr>
                    </thead>
                    <tbody>
                      {spamCalls.map((call) => (
                        <tr key={call.id} className="border-b border-gray-800 hover:bg-gray-900/50">
                          <td className="py-2 text-gray-400 text-xs">
                            {new Date(call.created_at ?? '').toLocaleString('it-IT')}
                          </td>
                          <td className="py-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              call.event_type === 'fail2ban_ban' ? 'bg-red-900 text-red-300' :
                              call.event_type === 'spoofed_call_blocked' ? 'bg-orange-900 text-orange-300' :
                              call.event_type === 'rate_limit_exceeded' ? 'bg-yellow-900 text-yellow-300' :
                              'bg-gray-700 text-gray-300'
                            }`}>
                              {call.event_type === 'fail2ban_ban' ? 'Fail2Ban' :
                               call.event_type === 'spoofed_call_blocked' ? 'Spoofing' :
                               call.event_type === 'rate_limit_exceeded' ? 'Rate Limit' :
                               call.event_type?.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="py-2">
                            <code className="bg-gray-800 px-1 rounded text-red-400">{call.ip_address}</code>
                          </td>
                          <td className="py-2 text-white text-xs">
                            {call.jail && <span className="text-purple-400">[{call.jail}] </span>}
                            {call.reason || call.caller_id || call.target || 'N/A'}
                          </td>
                          <td className="py-2 text-gray-400 text-xs">
                            {call.country ? (call.city ? `${call.city}, ${call.country}` : call.country) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="flex-1 text-sm">
                    <h4 className="font-semibold text-blue-900">Protezione Attiva</h4>
                    <p className="text-blue-700 mt-1">
                      Fail2Ban blocca automaticamente gli IP che tentano attacchi brute-force. 
                      Le chiamate spoofate da IP non autorizzati vengono bloccate.
                    </p>
                  </div>
                </div>
              </div>
              <IpBanCheckDialog open={ipBanDialogOpen} onOpenChange={setIpBanDialogOpen} />
            </TabsContent>

            <TabsContent value="observability" className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Activity className="h-5 w-5 text-purple-500" />
                  Metriche Sistema
                  <span className="flex items-center gap-1.5 text-xs font-normal text-muted-foreground ml-2">
                    <span className="h-2 w-2 bg-green-500 rounded-full animate-pulse" />
                    Auto-refresh 30s
                    {metricsLastUpdate && (
                      <span className="text-muted-foreground">
                        | Ultimo: {metricsLastUpdate.toLocaleTimeString()}
                      </span>
                    )}
                  </span>
                </h3>
                <Button variant="outline" size="sm" onClick={loadSocketMetrics} disabled={metricsLoading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${metricsLoading ? 'animate-spin' : ''}`} />
                  Aggiorna
                </Button>
              </div>

              {socketMetrics ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <Card className="border-l-4 border-l-green-500">
                      <CardContent className="pt-4">
                        <div className={`text-2xl font-bold ${socketMetrics.esl?.state === 'connected' ? 'text-green-600' : 'text-red-600'}`}>
                          {socketMetrics.esl?.state === 'connected' ? 'OK' : 'ERR'}
                        </div>
                        <div className="text-sm text-muted-foreground">ESL Status</div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-blue-500">
                      <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-blue-600">
                          {socketMetrics.socketIO?.totalConnections || 0}
                        </div>
                        <div className="text-sm text-muted-foreground">Socket.IO Conn</div>
                        <div className="text-xs text-muted-foreground">
                          {socketMetrics.socketIO?.authenticatedConnections || 0} auth
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-purple-500">
                      <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-purple-600">
                          {socketMetrics.calls?.activeCallsInMemory || 0}
                        </div>
                        <div className="text-sm text-muted-foreground">Chiamate Attive</div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-orange-500">
                      <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-orange-600">
                          {socketMetrics.esl?.reconnectAttempts || 0}
                        </div>
                        <div className="text-sm text-muted-foreground">ESL Reconnects</div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-yellow-500">
                      <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-yellow-600">
                          {(socketMetrics.dlq?.pendingRetry || 0) + (socketMetrics.dlq?.failed || 0)}
                        </div>
                        <div className="text-sm text-muted-foreground">DLQ Events</div>
                        <div className="text-xs text-muted-foreground">
                          {socketMetrics.dlq?.pendingRetry || 0} pending, {socketMetrics.dlq?.failed || 0} failed
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-cyan-500">
                      <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-cyan-600">
                          {socketMetrics.uptime?.formatted || '0s'}
                        </div>
                        <div className="text-sm text-muted-foreground">Uptime Backend</div>
                      </CardContent>
                    </Card>
                  </div>

                  {socketMetrics.esl?.eventCounts && Object.keys(socketMetrics.esl.eventCounts).length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm">Contatori Eventi ESL</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                          {Object.entries(socketMetrics.esl.eventCounts)
                            .sort(([,a], [,b]) => (b) - (a))
                            .map(([event, count]) => (
                              <div key={event} className="bg-muted rounded-lg p-2 text-center">
                                <div className="text-lg font-semibold">{count}</div>
                                <div className="text-xs text-muted-foreground truncate" title={event}>
                                  {event.replace('CHANNEL_', '').replace('_', ' ')}
                                </div>
                              </div>
                            ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {socketMetrics.calls?.callsByTenant && Object.keys(socketMetrics.calls.callsByTenant).length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm">Chiamate per Tenant</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {Object.entries(socketMetrics.calls.callsByTenant).map(([tenant, count]) => (
                            <div key={tenant} className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2 text-center">
                              <div className="text-lg font-semibold text-purple-600">{count}</div>
                              <div className="text-xs text-muted-foreground truncate" title={tenant}>
                                {tenant.substring(0, 8)}...
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <ToolMetrics />
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  {metricsLoading ? (
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
                  ) : (
                    <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  )}
                  <p>{metricsLoading ? 'Caricamento metriche...' : 'Nessuna metrica disponibile'}</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="queues" className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Layers className="h-5 w-5 text-indigo-500" />
                  Gestione Code
                  <span className="flex items-center gap-1.5 text-xs font-normal text-muted-foreground ml-2">
                    <span className="h-2 w-2 bg-green-500 rounded-full animate-pulse" />
                    Auto-refresh 15s
                    {queueLastUpdate && (
                      <span className="text-muted-foreground">
                        | Ultimo: {queueLastUpdate.toLocaleTimeString()}
                      </span>
                    )}
                  </span>
                </h3>
                <Button variant="outline" size="sm" onClick={() => { loadQueueStatus(); loadQueueJobs(); loadQueueHistory(); }} disabled={queueLoading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${queueLoading ? 'animate-spin' : ''}`} />
                  Aggiorna
                </Button>
              </div>

              {queueStatus?.alerts?.hasCritical && (
                <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-red-700 dark:text-red-400">Job bloccati rilevati</p>
                    <p className="text-sm text-red-600 dark:text-red-300">
                      {queueStatus.alerts.totalStale} job attivi da oltre 30 minuti. Verifica e intervieni.
                    </p>
                  </div>
                </div>
              )}

              {!queueStatus?.alerts?.hasCritical && queueStatus?.alerts?.hasWarning && (
                <div className="flex items-center gap-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-yellow-700 dark:text-yellow-400">Job falliti presenti</p>
                    <p className="text-sm text-yellow-600 dark:text-yellow-300">
                      {queueStatus.alerts.totalFailed} job falliti nelle code. Considera un retry o la pulizia.
                    </p>
                  </div>
                </div>
              )}

              {!queueStatus?.connected && (
                <div className="flex items-center gap-3 p-3 bg-muted border border-border rounded-lg">
                  <XCircle className="h-5 w-5 text-muted-foreground" />
                  <p className="text-muted-foreground">Redis non connesso — le code non sono disponibili</p>
                </div>
              )}

              {queueStatus?.connected && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {(queueStatus?.queues || []).map((q) => (
                      <Card key={q.name} className={`border-l-4 ${q.hasAlerts ? ((q.staleCount ?? 0) > 0 ? 'border-l-red-500' : 'border-l-yellow-500') : 'border-l-green-500'}`}>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm font-medium">{q.label}</CardTitle>
                            <div className="flex items-center gap-1">
                              {q.isPaused && (
                                <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-200">
                                  <Pause className="h-3 w-3 mr-1" /> Pausa
                                </Badge>
                              )}
                              {(q.staleCount ?? 0) > 0 && (
                                <Badge variant="destructive" className="text-xs">
                                  {q.staleCount} bloccati
                                </Badge>
                              )}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-5 gap-1.5 text-center mb-3">
                            <div>
                              <div className="text-base font-bold text-blue-600">{q.counts?.active || 0}</div>
                              <div className="text-[10px] text-muted-foreground">Attivi</div>
                            </div>
                            <div>
                              <div className="text-base font-bold text-yellow-600">{q.counts?.waiting || 0}</div>
                              <div className="text-[10px] text-muted-foreground">In attesa</div>
                            </div>
                            <div>
                              <div className="text-base font-bold text-green-600">{q.counts?.completed || 0}</div>
                              <div className="text-[10px] text-muted-foreground">Completati</div>
                            </div>
                            <div>
                              <div className="text-base font-bold text-red-600">{q.counts?.failed || 0}</div>
                              <div className="text-[10px] text-muted-foreground">Falliti</div>
                            </div>
                            <div>
                              <div className="text-base font-bold text-purple-600">{q.counts?.delayed || 0}</div>
                              <div className="text-[10px] text-muted-foreground">Ritardati</div>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 text-xs h-7"
                              disabled={queueActionLoading === `retry-${q.name}` || !((q.counts?.failed ?? 0) > 0)}
                              onClick={() => handleQueueAction(q.name, 'retry')}
                            >
                              <RotateCcw className="h-3 w-3 mr-1" />
                              Retry tutti
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 text-xs h-7 text-red-600 hover:text-red-700"
                              disabled={queueActionLoading === `clean-failed-${q.name}` || !((q.counts?.failed ?? 0) > 0)}
                              onClick={() => handleQueueAction(q.name, 'clean-failed')}
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Pulisci
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <Activity className="h-4 w-4 text-purple-500" />
                          Throughput Code
                        </CardTitle>
                        <div className="flex gap-2">
                          <Select value={queueHistoryHours} onValueChange={(v) => { setQueueHistoryHours(v); loadQueueHistory(v); }}>
                            <SelectTrigger className="w-28 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="6">6 ore</SelectItem>
                              <SelectItem value="12">12 ore</SelectItem>
                              <SelectItem value="24">24 ore</SelectItem>
                              <SelectItem value="48">48 ore</SelectItem>
                              <SelectItem value="168">7 giorni</SelectItem>
                              <SelectItem value="720">30 giorni</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button variant="outline" size="sm" className="h-8" onClick={() => loadQueueHistory()} disabled={queueHistoryLoading}>
                            <RefreshCw className={`h-3 w-3 ${queueHistoryLoading ? 'animate-spin' : ''}`} />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                          { key: 'workflows', label: 'Workflow Automazioni', color: '#8b5cf6' },
                          { key: 'mass-outreach', label: 'Invii Massivi SMS/WA', color: '#f59e0b' },
                          { key: 'mass-email', label: 'Email Massive', color: '#ec4899' },
                          { key: 'messaging', label: 'Messaggi Singoli', color: '#3b82f6' },
                        ].map(({ key, label, color }) => (
                          <div key={key}>
                            <p className="text-xs font-medium text-muted-foreground mb-2">{label}</p>
                            {(queueHistory[key] || []).length > 0 ? (
                              <ResponsiveContainer width="100%" height={160}>
                                <AreaChart data={queueHistory[key]}>
                                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                  <XAxis dataKey="label" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                                  <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
                                  <Tooltip contentStyle={{ fontSize: 12 }} />
                                  <Legend wrapperStyle={{ fontSize: 10 }} />
                                  <Area type="monotone" dataKey="completed" name="Completati" stroke="#22c55e" fill="#22c55e" fillOpacity={0.2} />
                                  <Area type="monotone" dataKey="failed" name="Falliti" stroke="#ef4444" fill="#ef4444" fillOpacity={0.2} />
                                </AreaChart>
                              </ResponsiveContainer>
                            ) : (
                              <div className="flex items-center justify-center h-[160px] text-muted-foreground">
                                <p className="text-xs">Nessun dato disponibile</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <CardTitle className="text-sm font-medium">Dettaglio Job</CardTitle>
                        <div className="flex items-center gap-2">
                          <Select value={queueSelectedQueue} onValueChange={(v) => { setQueueSelectedQueue(v); loadQueueJobs(v, queueSelectedStatus); }}>
                            <SelectTrigger className="w-[180px] h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="mass-outreach">Invii Massivi SMS/WA</SelectItem>
                              <SelectItem value="mass-email">Email Massive</SelectItem>
                              <SelectItem value="workflows">Workflow</SelectItem>
                              <SelectItem value="messaging">Messaggi</SelectItem>
                            </SelectContent>
                          </Select>
                          <Select value={queueSelectedStatus} onValueChange={(v) => { setQueueSelectedStatus(v); loadQueueJobs(queueSelectedQueue, v); }}>
                            <SelectTrigger className="w-[140px] h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="failed">Falliti</SelectItem>
                              <SelectItem value="active">Attivi</SelectItem>
                              <SelectItem value="waiting">In attesa</SelectItem>
                              <SelectItem value="delayed">Ritardati</SelectItem>
                              <SelectItem value="completed">Completati</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button variant="outline" size="sm" className="h-8" onClick={() => loadQueueJobs()} disabled={queueJobsLoading}>
                            <RefreshCw className={`h-3 w-3 ${queueJobsLoading ? 'animate-spin' : ''}`} />
                          </Button>
                        </div>
                      </div>
                      {queueJobsTotal > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">{queueJobsTotal} job totali con stato "{queueSelectedStatus}"</p>
                      )}
                    </CardHeader>
                    <CardContent>
                      {queueJobsLoading ? (
                        <div className="text-center py-8">
                          <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">Caricamento job...</p>
                        </div>
                      ) : queueJobs.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <Layers className="h-8 w-8 mx-auto mb-2 opacity-30" />
                          <p className="text-sm">Nessun job "{queueSelectedStatus}" trovato per questo tenant</p>
                          <p className="text-xs text-muted-foreground mt-1">Seleziona una coda e premi Aggiorna per caricare</p>
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-[500px] overflow-y-auto">
                          {queueJobs.map((job) => (
                            <div
                              key={job.id}
                              className={`border rounded-lg transition-all ${
                                job.isStale ? 'border-red-300 bg-red-50/50 dark:bg-red-900/10' :
                                job.status === 'failed' ? 'border-red-200 bg-red-50/30 dark:bg-red-900/5' :
                                'border-border'
                              }`}
                            >
                              <div
                                className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted rounded-lg"
                                onClick={() => setQueueExpandedJob(queueExpandedJob === job.id ? null : job.id)}
                              >
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    {job.isStale ? (
                                      <AlertTriangle className="h-4 w-4 text-red-500" />
                                    ) : job.status === 'failed' ? (
                                      <XCircle className="h-4 w-4 text-red-400" />
                                    ) : job.status === 'active' ? (
                                      <Activity className="h-4 w-4 text-blue-500 animate-pulse" />
                                    ) : job.status === 'waiting' ? (
                                      <Clock className="h-4 w-4 text-yellow-500" />
                                    ) : (
                                      <Check className="h-4 w-4 text-green-500" />
                                    )}
                                    <span className="text-xs font-mono text-muted-foreground">#{job.id}</span>
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium truncate">{job.data?.type || job.name}</span>
                                      {job.data?.channel && (
                                        <Badge variant="outline" className="text-xs">{job.data.channel}</Badge>
                                      )}
                                      {job.isStale && (
                                        <Badge variant="destructive" className="text-xs">BLOCCATO</Badge>
                                      )}
                                    </div>
                                    {job.data?.to && (
                                      <span className="text-xs text-muted-foreground">{job.data.leadName ? `${job.data.leadName} - ` : ''}{job.data.to}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">
                                    {job.timestamp ? new Date(job.timestamp).toLocaleString('it-IT', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : '-'}
                                  </span>
                                  {(job.status === 'failed' || job.isStale) && (
                                    <div className="flex gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        disabled={queueActionLoading === `retry-${job.id}`}
                                        onClick={(e) => { e.stopPropagation(); handleQueueAction(queueSelectedQueue, 'retry', job.id); }}
                                        title="Retry"
                                      >
                                        <RotateCcw className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                        disabled={queueActionLoading === `remove-job-${job.id}`}
                                        onClick={(e) => { e.stopPropagation(); handleQueueAction(queueSelectedQueue, 'remove-job', job.id); }}
                                        title="Rimuovi"
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  )}
                                  {queueExpandedJob === job.id ? (
                                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                  )}
                                </div>
                              </div>

                              {queueExpandedJob === job.id && (
                                <div className="px-3 pb-3 border-t border-border pt-2 space-y-1">
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                    <div>
                                      <span className="text-muted-foreground">Tentativi:</span>{' '}
                                      <span className="font-medium">{job.attempts}/{job.maxAttempts}</span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">Campaign:</span>{' '}
                                      <span className="font-medium">{job.data?.campaignId || '-'}</span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">Creato:</span>{' '}
                                      <span className="font-medium">{job.timestamp ? new Date(job.timestamp).toLocaleString('it-IT') : '-'}</span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">Completato:</span>{' '}
                                      <span className="font-medium">{job.finishedOn ? new Date(job.finishedOn).toLocaleString('it-IT') : '-'}</span>
                                    </div>
                                  </div>
                                  {job.failedReason && (
                                    <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs">
                                      <span className="font-medium text-red-700 dark:text-red-400">Errore: </span>
                                      <span className="text-red-600 dark:text-red-300 break-all">{job.failedReason}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {queueJobs.length > 0 && (
                        <div className="flex items-center justify-between mt-4 pt-3 border-t">
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              disabled={queueActionLoading === `retry-${queueSelectedQueue}`}
                              onClick={() => handleQueueAction(queueSelectedQueue, 'retry')}
                            >
                              <RotateCcw className="h-3 w-3 mr-1" />
                              Retry tutti i falliti
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs text-red-600 hover:text-red-700"
                              disabled={queueActionLoading === `clean-failed-${queueSelectedQueue}`}
                              onClick={() => handleQueueAction(queueSelectedQueue, 'clean-failed')}
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Pulisci falliti
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              disabled={queueActionLoading === `clean-completed-${queueSelectedQueue}`}
                              onClick={() => handleQueueAction(queueSelectedQueue, 'clean-completed')}
                            >
                              Pulisci completati
                            </Button>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {queueJobs.length} job visualizzati su {queueJobsTotal}
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>

            <TabsContent value="audit" className="space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="text-sm font-medium">Data Inizio</label>
                  <Input
                    type="date"
                    value={auditStartDate}
                    onChange={(e) => setAuditStartDate(e.target.value)}
                    className="w-40"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Data Fine</label>
                  <Input
                    type="date"
                    value={auditEndDate}
                    onChange={(e) => setAuditEndDate(e.target.value)}
                    className="w-40"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Azione</label>
                  <Select value={auditActionFilter} onValueChange={setAuditActionFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte</SelectItem>
                      <SelectItem value="create">Creazione</SelectItem>
                      <SelectItem value="update">Modifica</SelectItem>
                      <SelectItem value="delete">Eliminazione</SelectItem>
                      <SelectItem value="user_login">Login</SelectItem>
                      <SelectItem value="user_logout">Logout</SelectItem>
                      <SelectItem value="config_change">Config</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Tipo Risorsa</label>
                  <Select value={auditResourceFilter} onValueChange={setAuditResourceFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti</SelectItem>
                      {auditResourceTypes.map((rt) => (
                        <SelectItem key={rt} value={rt}>{rt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Cerca</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Cerca..."
                      value={auditSearch}
                      onChange={(e) => setAuditSearch(e.target.value)}
                      className="pl-10 w-48"
                    />
                  </div>
                </div>
                <Button onClick={() => loadAuditLogs(1)} disabled={auditLoading}>
                  <Search className="h-4 w-4 mr-1" />
                  Filtra
                </Button>
                <Button variant="outline" onClick={handleAuditExport}>
                  <Download className="h-4 w-4 mr-1" />
                  Export CSV
                </Button>
              </div>

              {auditLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-muted-foreground">Caricamento...</span>
                </div>
              ) : auditLogs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Shield className="h-12 w-12 mb-4 opacity-50" />
                  <p className="text-lg font-medium">Nessun log di audit</p>
                  <p className="text-sm">I log di audit verranno visualizzati quando disponibili.</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted">
                          <th className="text-left p-3 font-medium">Data/Ora</th>
                          <th className="text-left p-3 font-medium">Utente</th>
                          <th className="text-left p-3 font-medium">Azione</th>
                          <th className="text-left p-3 font-medium">Tipo Risorsa</th>
                          <th className="text-left p-3 font-medium">Risorsa</th>
                          <th className="text-left p-3 font-medium">IP</th>
                          <th className="text-left p-3 font-medium">Status</th>
                          <th className="text-left p-3 font-medium">Dettagli</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditLogs.map((log) => (
                          <>
                            <tr key={log.id} className="border-b hover:bg-muted cursor-pointer" onClick={() => setAuditExpandedRow(auditExpandedRow === log.id ? null : log.id)}>
                              <td className="p-3 whitespace-nowrap text-muted-foreground">
                                {new Date(log.created_at ?? '').toLocaleString('it-IT')}
                              </td>
                              <td className="p-3">
                                <span className="text-foreground">{log.user_id?.slice(0, 8) || '-'}</span>
                              </td>
                              <td className="p-3">
                                <Badge className={`text-xs ${
                                  log.action === 'delete' ? 'bg-red-100 text-red-800' :
                                  log.action === 'create' ? 'bg-green-100 text-green-800' :
                                  log.action === 'update' ? 'bg-blue-100 text-blue-800' :
                                  log.action === 'user_login' ? 'bg-purple-100 text-purple-800' :
                                  log.action === 'user_logout' ? 'bg-orange-100 text-orange-800' :
                                  'bg-muted text-foreground'
                                }`}>
                                  {getActionLabel(log.action)}
                                </Badge>
                              </td>
                              <td className="p-3 text-muted-foreground">{log.resource_type || '-'}</td>
                              <td className="p-3 text-muted-foreground max-w-[150px] truncate">{log.resource_id || '-'}</td>
                              <td className="p-3 text-muted-foreground text-xs font-mono">{log.ip_address || '-'}</td>
                              <td className="p-3">{getStatusBadge(log.status_code)}</td>
                              <td className="p-3">
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                  {auditExpandedRow === log.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                </Button>
                              </td>
                            </tr>
                            {auditExpandedRow === log.id && (
                              <tr key={`${log.id}-detail`} className="bg-muted">
                                <td colSpan={8} className="p-4">
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                    <div>
                                      <span className="font-medium text-muted-foreground">Metodo:</span>
                                      <span className="ml-1 text-foreground">{log.request_method || '-'}</span>
                                    </div>
                                    <div>
                                      <span className="font-medium text-muted-foreground">Path:</span>
                                      <span className="ml-1 text-foreground break-all">{log.request_path || '-'}</span>
                                    </div>
                                    <div>
                                      <span className="font-medium text-muted-foreground">Durata:</span>
                                      <span className="ml-1 text-foreground">{log.duration_ms != null ? `${log.duration_ms}ms` : '-'}</span>
                                    </div>
                                    <div>
                                      <span className="font-medium text-muted-foreground">User Agent:</span>
                                      <span className="ml-1 text-foreground break-all">{log.user_agent?.slice(0, 60) || '-'}</span>
                                    </div>
                                    {log.error_message && (
                                      <div className="col-span-4">
                                        <span className="font-medium text-red-500">Errore:</span>
                                        <span className="ml-1 text-red-700">{log.error_message}</span>
                                      </div>
                                    )}
                                    {!!log.old_values && (
                                      <div className="col-span-2">
                                        <span className="font-medium text-muted-foreground">Valori Precedenti:</span>
                                        <pre className="mt-1 p-2 bg-card rounded text-xs overflow-auto max-h-32">{JSON.stringify(log.old_values, null, 2)}</pre>
                                      </div>
                                    )}
                                    {!!log.new_values && (
                                      <div className="col-span-2">
                                        <span className="font-medium text-muted-foreground">Nuovi Valori:</span>
                                        <pre className="mt-1 p-2 bg-card rounded text-xs overflow-auto max-h-32">{JSON.stringify(log.new_values, null, 2)}</pre>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <span className="text-sm text-muted-foreground">
                      Pagina {auditPage} di {auditTotalPages} ({auditTotal} risultati)
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={auditPage <= 1 || auditLoading}
                        onClick={() => loadAuditLogs(auditPage - 1)}
                      >
                        Precedente
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={auditPage >= auditTotalPages || auditLoading}
                        onClick={() => loadAuditLogs(auditPage + 1)}
                      >
                        Successiva
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
